// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { authAPI, questionsAPI, formationsAPI, plateformesAPI, domainesExpertiseAPI } from './api/api';
import { AuthProvider, useAuth } from './context/AuthContext';
import Login from './views/Login';
import Register from './views/Register';
import Dashboard from './views/Dashboard';
import QuestionsList from './views/QuestionsList';
import CreateQuestion from './views/CreateQuestion';
import FormationsList from './views/FormationsList';
import PlateformesList from './views/PlateformesList';
import ImportQuestions from './views/ImportQuestions';
import ExportQuestions from './views/ExportQuestions';
import UsersList from './views/UsersList';
import DomainesExpertiseList from './views/DomainesExpertiseList';
import FormationProgramme from './views/FormationProgramme';
import RessourcesList from './views/RessourcesList';
import RessourcesPubliques from './views/RessourcesPubliques';
import RessourcePublique from './views/RessourcePublique';
import Navbar from './components/Navbar';

// Composant wrapper pour les routes protégées
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  return children;
};

// Composant wrapper pour les routes publiques
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (user) {
    return <Navigate to="/dashboard" />;
  }

  return children;
};

// Composant pour la route d'édition avec paramètres
const EditQuestionRoute = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        try {
          const formationsData = await formationsAPI.getAll();
          setFormations(formationsData);
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [user]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return <CreateQuestion formations={formations} />;
};

function AppContent() {
  const { user } = useAuth();
  const [formations, setFormations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Charger les formations
  useEffect(() => {
    if (user) {
      const fetchData = async () => {
        try {
          const formationsData = await formationsAPI.getAll();
          setFormations(formationsData);
        } catch (error) {
          console.error('Erreur lors du chargement des données:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setLoading(false);
    }
  }, [user]);

  if (loading && user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {user && <Navbar />}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <Routes>
          <Route path="/" element={<Navigate to="/dashboard" />} />
          <Route path="/login" element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          } />
          <Route path="/dashboard" element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          } />
          <Route path="/questions" element={
            <ProtectedRoute>
              <QuestionsList formations={formations} />
            </ProtectedRoute>
          } />
          <Route path="/questions/create" element={
            <ProtectedRoute>
              <CreateQuestion formations={formations} />
            </ProtectedRoute>
          } />
          <Route path="/questions/edit/:id" element={
            <ProtectedRoute>
              <EditQuestionRoute />
            </ProtectedRoute>
          } />
          <Route path="/formations" element={
            <ProtectedRoute>
              <FormationsList />
            </ProtectedRoute>
          } />
          <Route path="/formations/:id/programme" element={
            <ProtectedRoute>
              <FormationProgramme />
            </ProtectedRoute>
          } />
          <Route path="/domaines-expertise" element={
            <ProtectedRoute>
              <DomainesExpertiseList />
            </ProtectedRoute>
          } />
          <Route path="/plateformes" element={
            <ProtectedRoute>
              <PlateformesList />
            </ProtectedRoute>
          } />
          <Route path="/import-questions" element={
            <ProtectedRoute>
              <ImportQuestions />
            </ProtectedRoute>
          } />
          <Route path="/export-questions" element={
            <ProtectedRoute>
              <ExportQuestions />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute>
              <UsersList />
            </ProtectedRoute>
          } />
          <Route path="/ressources" element={
            <ProtectedRoute>
              <RessourcesList />
            </ProtectedRoute>
          } />
        </Routes>
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/partage/ressources/:token" element={<RessourcesPubliques />} />
          <Route path="/partage/ressource/:token" element={<RessourcePublique />} />
          <Route path="*" element={<AppContent />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;